// src/pages/MobiOfficeSupport.tsx
import "./support.scss";
import React, { useRef, useState } from "react";
import { getVendorId } from "../../api/sessions";
import { TicketModel } from "../../types/index";
import { TicketList } from "./TicketList";
import { NewTicketForm, NewTicketFormHandle } from "./NewTicketForm";
import { TicketDetail } from "./TicketDetail";
import Button from "devextreme-react/button";
import { useTranslation } from "react-i18next";

export const MobiOfficeSupport: React.FC = () => {
  const vendorId = getVendorId();
  const [selectedTicket, setSelectedTicket] = useState<TicketModel | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [ticketCount, setTicketCount] = useState(0);
  const [showSidebar, setShowSidebar] = useState(true);
  const [creatingTicket, setCreatingTicket] = useState(false);
  const newTicketFormRef = useRef<NewTicketFormHandle | null>(null);
  const { t } = useTranslation();

  if (!vendorId) return <div>{t("vendorIdNotFound")}</div>;

  return (
    <div className="flex w-full min-h-0 support-surface" style={{ height: 'calc(100% - 30px)' }}>
      {showSidebar && (
        <aside className="w-full md:w-[420px] border-r flex flex-col min-h-0 support-surface support-border">
          <div className="support-sidebar-header">
            <i className="dx-icon dx-icon-help" style={{ fontSize: 18, color: 'var(--accent-color)' }} />
            <div className="font-semibold">{t("supportTickets")}</div>
            <div className="ml-auto text-xs support-text-muted">
              {ticketCount} {t("records")}
            </div>
            <Button
              icon="plus"
              stylingMode="text"
              hint={t("newSupportTicket")}
              onClick={() => {
                setShowForm(true);
                setSelectedTicket(null);
                if (window.innerWidth < 768) setShowSidebar(false);
              }}
            />
            <Button
              icon="chevrondoubleleft"
              stylingMode="text"
              hint={t("hidePanel")}
              onClick={() => setShowSidebar(false)}
            />
          </div>

          <div className="flex-1 min-h-0 p-2 support-surface-muted overflow-auto">
            <TicketList
              vendorId={vendorId}
              selectedId={selectedTicket?.id}
              onSelect={(t) => {
                setSelectedTicket(t);
                setShowForm(false);
                if (window.innerWidth < 768) setShowSidebar(false);
              }}
              onCountChange={setTicketCount}
            />
          </div>
        </aside>
      )}

      <section className="flex-1 flex flex-col min-h-0">
        <div className="border-b p-2 flex items-center gap-2">
          {!showSidebar && (
            <Button
              icon="chevrondoubleright"
              stylingMode="text"
              hint={t("showPanel")}
              onClick={() => setShowSidebar(true)}
            />
          )}

          <div className="font-semibold truncate p-1.5">
            {showForm ? t("newSupportTicket") : selectedTicket?.title ?? t("support")}
          </div>

          <div className="ml-auto flex items-center gap-2">
            {showForm && (
              <Button
                icon="send"
                text={creatingTicket ? t("sending") : t("createTicket")}
                type="success"
                stylingMode="contained"
                disabled={creatingTicket}
                onClick={() => newTicketFormRef.current?.submit()}
              />
            )}
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-hidden p-3 md:p-4 support-surface-muted">
          <div className="h-full min-h-0">
            {showForm && (
              <NewTicketForm
                ref={newTicketFormRef}
                onCreated={() => setShowForm(false)}
                onSavingChange={setCreatingTicket}
              />
            )}
            {!showForm && selectedTicket && (
              <TicketDetail ticket={selectedTicket} vendorId={vendorId} />
            )}
            {!showForm && ticketCount > 0 && !selectedTicket && (
              <div className="support-empty-state">
                <i className="dx-icon dx-icon-selectall" />
                <span className="text-sm support-text-muted">{t("selectTicketFromLeft")}</span>
              </div>
            )}
            {!showForm && ticketCount === 0 && !selectedTicket && (
              <div className="support-empty-state">
                <i className="dx-icon dx-icon-help" />
                <span className="text-sm support-text-muted">{t("noData")}</span>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};
